import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PWAStatus = () => {
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const handleBeforeInstallPrompt = (e) => {
      e?.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    if (window.matchMedia('(display-mode: standalone)')?.matches) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    setIsInstalling(true);
    deferredPrompt?.prompt();
    const { outcome } = await deferredPrompt?.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setIsInstalling(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Smartphone" size={18} />
          Progressive Web App
        </h3>
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Download" size={24} color="var(--color-primary)" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-semibold text-foreground mb-2">
                {isInstalled ? 'App Installed' : 'Install Desktop App'}
              </h4>
              <p className="text-sm text-muted-foreground mb-4">
                {isInstalled 
                  ? 'S3 File Manager is installed and ready to use offline' :'Install S3 File Manager as a desktop application for quick access and offline capabilities'}
              </p>
              {!isInstalled && deferredPrompt && (
                <Button
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleInstall}
                  loading={isInstalling}
                >
                  Install Now
                </Button>
              )}
              {isInstalled && (
                <div className="flex items-center gap-2 text-success">
                  <Icon name="CheckCircle2" size={18} />
                  <span className="text-sm font-medium">Successfully Installed</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <div className="flex items-center gap-3">
                <Icon name={isOnline ? 'Wifi' : 'WifiOff'} size={18} className={isOnline ? 'text-success' : 'text-error'} />
                <div>
                  <p className="text-sm font-medium text-foreground">Connection Status</p>
                  <p className="text-xs text-muted-foreground">{isOnline ? 'Online' : 'Offline'}</p>
                </div>
              </div>
              <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-success' : 'bg-error'} animate-pulse`} />
            </div>

            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <div className="flex items-center gap-3">
                <Icon name="HardDrive" size={18} className="text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Offline Storage</p>
                  <p className="text-xs text-muted-foreground">12.5 MB cached</p>
                </div>
              </div>
              <span className="text-xs font-medium text-foreground font-mono">85% available</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <div className="flex items-center gap-3">
                <Icon name="RefreshCw" size={18} className="text-primary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Last Sync</p>
                  <p className="text-xs text-muted-foreground">2 minutes ago</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" iconName="RefreshCw">
                Sync
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Zap" size={18} />
          Offline Features
        </h3>
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
            <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
            <p className="text-sm text-foreground">Browse cached files and folders</p>
          </div>
          <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
            <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
            <p className="text-sm text-foreground">View file previews and metadata</p>
          </div>
          <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
            <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
            <p className="text-sm text-foreground">Queue uploads for when online</p>
          </div>
          <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
            <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
            <p className="text-sm text-foreground">Access recent file history</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAStatus;